import { Bookmark, BookmarkCheck, FlaskConical, Heart, HeartHandshake, Share2, Target, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

interface ProjectHeroProps {
  project: {
    id: number
    title: string
    description: string
    problem_statement: string
    project_type: 'mission' | 'research'
    status: 'planning' | 'active' | 'paused' | 'completed'
    category: string
    owner_name: string
    budget_flz: number
    spent_flz: number
    progress: number
    members_count: number
    agents_count: number
    tasks_count: number
    findings_count: number
    questions_count: number
    donors_count: number
    followers_count: number
    cycles_count: number
    likes_count?: number
    saves_count?: number
    created_at: string
    updated_at: string
  }
  isOwner: boolean
  onDonateClick?: () => void
}

interface Engagement {
  likes_count: number
  saves_count: number
  follows_count: number
  is_liked: boolean
  is_saved: boolean
  is_following: boolean
}

export default function ProjectHero({ project, isOwner }: ProjectHeroProps) {
  const { user } = useAuth()
  const [engagement, setEngagement] = useState<Engagement>({
    likes_count: project.likes_count || 0,
    saves_count: project.saves_count || 0,
    follows_count: project.followers_count || 0,
    is_liked: false,
    is_saved: false,
    is_following: false,
  })
  const [donateAmount, setDonateAmount] = useState('')
  const [showDonateBox, setShowDonateBox] = useState(false)
  const [donating, setDonating] = useState(false)
  const [donateMsg, setDonateMsg] = useState('')

  const isResearch = project.project_type === 'research'
  const budgetPct = project.budget_flz > 0
    ? Math.min(100, (project.spent_flz / project.budget_flz) * 100)
    : 0
  const remaining = project.budget_flz - project.spent_flz
  const isBudgetLow = remaining < project.budget_flz * 0.15

  useEffect(() => {
    if (!user) {return}
    void fetch(`${API_URL}/api/projects/${project.id}/engagement?user_id=${user.id}`)
      .then(r => r.json())
      .then((d: Engagement) => setEngagement(d))
      .catch(() => {})
  }, [project.id, user])

  const handleLike = async () => {
    if (!user) {return}
    const res = await fetch(`${API_URL}/api/projects/${project.id}/like?user_id=${user.id}`, { method: 'POST' })
    if (res.ok) {
      const d = await res.json() as { liked: boolean; likes_count: number }
      setEngagement(prev => ({ ...prev, is_liked: d.liked, likes_count: d.likes_count }))
    }
  }

  const handleSave = async () => {
    if (!user) {return}
    const res = await fetch(`${API_URL}/api/projects/${project.id}/save?user_id=${user.id}`, { method: 'POST' })
    if (res.ok) {
      const d = await res.json() as { saved: boolean; saves_count: number }
      setEngagement(prev => ({ ...prev, is_saved: d.saved, saves_count: d.saves_count }))
    }
  }

  const handleFollow = async () => {
    if (!user) {return}
    const res = await fetch(`${API_URL}/api/projects/${project.id}/follow?user_id=${user.id}`, { method: 'POST' })
    if (res.ok) {
      const d = await res.json() as { following: boolean; follows_count: number }
      setEngagement(prev => ({ ...prev, is_following: d.following, follows_count: d.follows_count }))
    }
  }

  const handleDonate = async () => {
    if (!user || !donateAmount) {return}
    const amount = parseFloat(donateAmount)
    if (isNaN(amount) || amount <= 0) {return}
    setDonating(true)
    try {
      const res = await fetch(
        `${API_URL}/api/projects/${project.id}/donate?user_id=${user.id}&amount_flz=${amount}`,
        { method: 'POST' }
      )
      const d = await res.json() as { status?: string; message?: string; detail?: string; new_balance?: number }
      if (res.ok) {
        setDonateMsg(`Thank you! ${amount} FLZ donated. New balance: ${d.new_balance?.toFixed(2)} FLZ`)
        setDonateAmount('')
        setTimeout(() => { setShowDonateBox(false); setDonateMsg('') }, 3000)
      } else {
        setDonateMsg(d.detail || 'Donation failed')
      }
    } catch {
      setDonateMsg('Network error')
    } finally {
      setDonating(false)
    }
  }

  const [shareMsg, setShareMsg] = useState('')

  const handleShare = () => {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setShareMsg('Link copied!')
      setTimeout(() => setShareMsg(''), 2000)
    })
  }

  const statusColor: Record<string, string> = {
    active: '#10b981',
    planning: '#f59e0b',
    paused: '#ef4444',
    completed: '#6366f1',
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Budget depletion banner */}
      {isBudgetLow && project.status !== 'completed' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '12px',
          padding: '14px 20px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <HeartHandshake size={20} style={{ color: '#ef4444', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>
              {project.status === 'paused'
                ? 'This research is paused — budget depleted. Donate FLZ to bring it back to life.'
                : `Only ${remaining.toFixed(0)} FLZ remaining. Help keep this research running.`}
            </span>
          </div>
          <button
            onClick={() => setShowDonateBox(true)}
            style={{
              padding: '8px 20px',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Donate FLZ →
          </button>
        </div>
      )}

      {/* Main Hero Card */}
      <div style={{
        background: 'var(--card-bg)',
        border: `1px solid ${isResearch ? '#8b5cf6' : '#3b82f6'}`,
        borderRadius: '16px',
        padding: '28px',
        display: 'flex',
        gap: '28px',
        flexWrap: 'wrap',
      }}>
        {/* Left */}
        <div style={{ flex: 1, minWidth: '280px' }}>
          {/* Type badge + status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '20px',
              background: isResearch ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: '600',
            }}>
              {isResearch ? <FlaskConical size={13} /> : <Target size={13} />}
              {project.category || 'General'} · {isResearch ? 'Research' : 'Mission'}
            </span>
            <span style={{
              padding: '3px 10px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: `${statusColor[project.status] || '#6366f1'}20`,
              color: statusColor[project.status] || '#6366f1',
            }}>
              {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </span>
          </div>

          <h1 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '10px', lineHeight: '1.3' }}>
            {project.title}
          </h1>

          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.6', marginBottom: '20px' }}>
            {project.problem_statement || project.description}
          </p>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {[
              { label: 'Budget', value: `${project.budget_flz.toLocaleString()} FLZ` },
              { label: 'Findings', value: project.findings_count || 0 },
              { label: 'Cycles', value: project.cycles_count || 0 },
              { label: 'Followers', value: engagement.follows_count },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Budget progress bar */}
          {isResearch && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                <span>Budget used: {budgetPct.toFixed(1)}%</span>
                <span style={{ color: isBudgetLow ? '#ef4444' : 'var(--text-secondary)' }}>
                  {remaining.toFixed(0)} FLZ remaining
                </span>
              </div>
              <div style={{ height: '8px', background: 'var(--border-color)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${budgetPct}%`,
                  background: isBudgetLow
                    ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                    : 'linear-gradient(90deg, #8b5cf6, #7c3aed)',
                  borderRadius: '4px',
                  transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {!isOwner && (
              <button
                onClick={() => setShowDonateBox(!showDonateBox)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                }}
              >
                <HeartHandshake size={16} />
                Donate FLZ
              </button>
            )}
            <button
              onClick={() => void handleLike()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px',
                background: engagement.is_liked ? 'rgba(239,68,68,0.1)' : 'var(--hover-bg, #f8fafc)',
                color: engagement.is_liked ? '#ef4444' : 'var(--text-secondary)',
                border: `1px solid ${engagement.is_liked ? '#ef4444' : 'var(--border-color)'}`,
                borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              <Heart size={15} fill={engagement.is_liked ? '#ef4444' : 'none'} />
              {engagement.likes_count}
            </button>
            <button
              onClick={() => void handleSave()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px',
                background: engagement.is_saved ? 'rgba(102,126,234,0.1)' : 'var(--hover-bg, #f8fafc)',
                color: engagement.is_saved ? '#667eea' : 'var(--text-secondary)',
                border: `1px solid ${engagement.is_saved ? '#667eea' : 'var(--border-color)'}`,
                borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              {engagement.is_saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />}
              Save
            </button>
            <button
              onClick={() => void handleFollow()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px',
                background: engagement.is_following ? 'rgba(16,185,129,0.1)' : 'var(--hover-bg, #f8fafc)',
                color: engagement.is_following ? '#10b981' : 'var(--text-secondary)',
                border: `1px solid ${engagement.is_following ? '#10b981' : 'var(--border-color)'}`,
                borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              <Users size={15} />
              {engagement.is_following ? 'Following' : 'Follow'}
            </button>
            <button
              onClick={handleShare}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 16px',
                background: shareMsg ? 'rgba(16,185,129,0.1)' : 'var(--hover-bg, #f8fafc)',
                color: shareMsg ? '#10b981' : 'var(--text-secondary)',
                border: `1px solid ${shareMsg ? '#10b981' : 'var(--border-color)'}`,
                borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              }}
            >
              <Share2 size={15} />
              {shareMsg || 'Share'}
            </button>
          </div>

          {/* Donate box */}
          {showDonateBox && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'var(--hover-bg, #f8fafc)',
              border: '1px solid var(--border-color)',
              borderRadius: '10px',
            }}>
              <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px', color: 'var(--text-primary)' }}>
                Support this research with FLZ
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[100, 250, 500, 1000].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setDonateAmount(String(amt))}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      border: donateAmount === String(amt) ? '2px solid #667eea' : '1px solid var(--border-color)',
                      background: donateAmount === String(amt) ? 'rgba(102,126,234,0.1)' : 'transparent',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    {amt} FLZ
                  </button>
                ))}
                <input
                  type="number"
                  placeholder="Custom amount"
                  value={donateAmount}
                  onChange={e => setDonateAmount(e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--card-bg)',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                    width: '130px',
                  }}
                />
                <button
                  onClick={() => void handleDonate()}
                  disabled={donating || !donateAmount}
                  style={{
                    padding: '6px 18px',
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: donating ? 'not-allowed' : 'pointer',
                    opacity: donating ? 0.7 : 1,
                  }}
                >
                  {donating ? 'Sending...' : 'Donate'}
                </button>
              </div>
              {donateMsg && (
                <div style={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: donateMsg.includes('Thank') ? '#10b981' : '#ef4444',
                  fontWeight: '500',
                }}>
                  {donateMsg}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: visual */}
        <div style={{
          width: '200px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          flexShrink: 0,
        }}>
          <div style={{
            width: '140px',
            height: '140px',
            borderRadius: '50%',
            background: isResearch
              ? 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(124,58,237,0.05))'
              : 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(37,99,235,0.05))',
            border: `2px solid ${isResearch ? '#8b5cf6' : '#3b82f6'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '60px',
          }}>
            {isResearch ? '🔬' : '🎯'}
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>by</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>{project.owner_name}</div>
          </div>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        marginTop: '12px',
        padding: '12px 18px',
        background: 'rgba(102,126,234,0.05)',
        border: '1px solid rgba(102,126,234,0.2)',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        fontSize: '13px',
        color: 'var(--text-secondary)',
        lineHeight: '1.5',
      }}>
        <span style={{ fontSize: '16px', flexShrink: 0 }}>ℹ️</span>
        <span>
          <strong style={{ color: 'var(--text-primary)' }}>RunAI</strong> connects researchers with AI agents and supporters.
          Results are not guaranteed, but agents are required to submit regular findings.
          Your FLZ contribution directly funds AI compute for this research.
        </span>
      </div>
    </div>
  )
}
